import React, { useState, useEffect } from 'react';
import api from './api';
import useIsMobile from './hooks/useIsMobile';

const PioneerLaunchPage = ({ onBack, onSignUp }) => {
  const [stats, setStats] = useState({ claimed: 0, total: 100 });
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get('/pioneers/stats')
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  const benefits = [
    { icon: '★', title: 'Atlas Pioneer Elite Badge', desc: 'A permanent badge on your profile marking you as a founding member' },
    { icon: '🔥', title: 'Pioneer Circle Access', desc: 'Direct line to the founders — help shape the future of Runrora' },
    { icon: '💡', title: 'Shape the Platform', desc: 'Your feedback directly influences how runners connect globally' },
    { icon: '🆓', title: 'Always Free', desc: 'Pioneer Elite members will always have free access to Runrora' },
  ];

  const referralSteps = [
    { num: '1', title: 'Join Runrora', desc: 'Sign up and claim your Pioneer Elite spot' },
    { num: '2', title: 'Share Your Link', desc: 'Get a unique referral link from your dashboard' },
    { num: '3', title: 'Unlock Community Builder', desc: 'When 10 of your referrals are active, earn Community Builder status and free gear' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)', borderBottom: '1px solid rgba(255, 138, 61, 0.2)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer' }}>←</button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Atlas Pioneer Elite</h1>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '3rem 1.5rem' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: isMobile ? '1.75rem' : '2.5rem', fontWeight: 800, margin: '0 0 0.75rem', lineHeight: 1.2 }}>
            Be One of the First <span style={{ color: '#FF8A3D' }}>100</span>
          </h2>
          <p style={{ color: '#8b9cb3', fontSize: '1.1rem', margin: '0 0 1.5rem', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
            The first 100 runners to join Runrora become Atlas Pioneer Elite — our most exclusive founding members.
          </p>
          {/* Counter */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(255, 138, 61, 0.1)',
            border: '1px solid rgba(255, 138, 61, 0.3)',
            borderRadius: '25px',
            padding: '0.5rem 1.25rem',
            fontSize: '1rem',
            fontWeight: 600,
          }}>
            <span style={{ color: '#FF8A3D', fontSize: '1.25rem' }}>{stats.claimed}</span>
            <span style={{ color: '#8b9cb3' }}>/ {stats.total} spots claimed</span>
          </div>
        </div>

        {/* Benefits */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#FF8A3D' }}>What You Get</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
            {benefits.map(b => (
              <div key={b.title} style={{
                background: '#16161D',
                border: '1px solid rgba(255, 138, 61, 0.1)',
                borderRadius: '12px',
                padding: '1.25rem',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{b.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{b.title}</div>
                  <div style={{ color: '#8b9cb3', fontSize: '0.85rem' }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pioneer Challenge */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: '#FF8A3D' }}>The Pioneer Challenge</h3>
          <p style={{ color: '#8b9cb3', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Invite fellow runners and help them become active. Reach 10 active referrals to unlock Community Builder status and free Runrora gear.
          </p>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.75rem' }}>
            {referralSteps.map(s => (
              <div key={s.num} style={{
                flex: 1,
                background: '#16161D',
                border: '1px solid rgba(255, 138, 61, 0.1)',
                borderRadius: '12px',
                padding: '1.25rem',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 138, 61, 0.2)',
                  color: '#FF8A3D',
                  fontWeight: 700,
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                }}>
                  {s.num}
                </div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.9rem' }}>{s.title}</div>
                <div style={{ color: '#8b9cb3', fontSize: '0.8rem' }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <button
            onClick={onSignUp}
            style={{
              padding: '0.85rem 2.5rem',
              background: '#FF8A3D',
              color: '#0B0B10',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Claim Your Pioneer Elite Spot
          </button>
        </div>
      </div>
    </div>
  );
};

export default PioneerLaunchPage;
